import { useCallback, useRef, useState } from "react";
import { Button, Col, Divider, Form, FormInstance, Input, Modal, notification, Row, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";

import { IDefaultInputInstruction, IDefaultMessage } from "../../interfaces";

const OPTIONS = ["Mensagens", "Instruções do campo de resposta"];

interface IProps {
  visible: boolean,
  onClose: () => void;
  messages: IDefaultMessage[];
  inputInstructions: IDefaultInputInstruction[];
  onSubmitMessage: (data: Omit<IDefaultMessage, "_id">) => Promise<boolean>;
  onSubmitInputInstruction: (data: Omit<IDefaultInputInstruction, "_id">) => Promise<boolean>;
}

export default function CadastroModal({
  visible,
  onClose,
  inputInstructions,
  messages,
  onSubmitInputInstruction,
  onSubmitMessage,
}: IProps) {
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const messageFormRef = useRef<FormInstance>(null);
  const instructionsFormRef = useRef<FormInstance>(null);

  const handleSubmitMessage = useCallback(async (values: Omit<IDefaultMessage, "_id">) => {
    setIsSubmiting(true);

    const success = await onSubmitMessage(values);

    if (success) {
      messageFormRef.current?.resetFields();
      notification.success({
        message: "Mensagem cadastrada com sucesso!"
      })
    }

    setIsSubmiting(false);
  }, []);

  const handleSubmitInputInstruction = useCallback(async (values: Omit<IDefaultInputInstruction, "_id">) => {
    setIsSubmiting(true);

    const success = await onSubmitInputInstruction(values);

    if (success) {
      instructionsFormRef.current?.resetFields();
      notification.success({
        message: "Instrução cadastrada com sucesso!"
      })
    }

    setIsSubmiting(false);
  }, []);

  return (
    <Modal
      visible={visible}
      destroyOnClose
      afterClose={() => setSelectedOption(null)}
      onCancel={onClose}
      closable={false}
      okButtonProps={{
        hidden: true
      }}
      cancelText="Fechar"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4
        }}
      >
        {
          !selectedOption && OPTIONS.map((item) => (
            <div
              key={item}
              onClick={() => setSelectedOption(item)}
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px",
                cursor: "pointer"
              }}
            >
              <Typography.Text>{item}</Typography.Text>

              <RightOutlined />
            </div>
          ))
        }

        {
          selectedOption === "Mensagens" && (
            <>
              <Typography.Title level={4}>
                {selectedOption}
              </Typography.Title>

              {
                messages.map((message) => (
                  <div key={message.title}>
                    <Typography.Text>
                      {message.title}
                    </Typography.Text>

                    <div
                      style={{
                        marginLeft: 16,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <Typography.Text>
                        - {message.message}
                      </Typography.Text>
                    </div>
                  </div>
                ))
              }


              <Divider />

              <Typography.Title level={5}>
                Adicionar nova mensagem
              </Typography.Title>

              <Form
                ref={messageFormRef}
                name='messageForm'
                layout="vertical"
                onFinish={handleSubmitMessage}
              >
                <Col span={24} >
                  <Form.Item
                    name="title"
                    label="Título"
                    rules={[
                      {
                        required: true,
                        message: 'Digite o título',
                      },
                    ]}
                  >
                    <Input placeholder="Digite o título" />
                  </Form.Item>
                </Col>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="message"
                      label="Mensagem"
                      rules={[
                        {
                          required: true,
                          message: 'Digite a mensagem',
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Digite a mensagem"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmiting}
                >
                  Adicionar
                </Button>
              </Form>
            </>
          )
        }

        {
          selectedOption === "Instruções do campo de resposta" && (
            <>
              <Typography.Title level={4}>
                {selectedOption}
              </Typography.Title>

              {
                inputInstructions.map((instruction) => (
                  <div key={instruction.title}>
                    <Typography.Text>
                      {instruction.title}
                    </Typography.Text>

                    <div
                      style={{
                        marginLeft: 16
                      }}
                    >
                      <Typography.Text>
                        - {instruction.message}
                      </Typography.Text>
                    </div>
                  </div>
                ))
              }

              <Divider />

              <Typography.Title level={5}>
                Adicionar nova instrução
              </Typography.Title>

              <Form
                ref={instructionsFormRef}
                name='instructionForm'
                layout="vertical"
                onFinish={handleSubmitInputInstruction}
              >
                <Col span={24} >
                  <Form.Item
                    name="title"
                    label="Título"
                    rules={[
                      {
                        required: true,
                        message: 'Digite o título',
                      },
                    ]}
                  >
                    <Input placeholder="Digite o título" />
                  </Form.Item>
                </Col>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="message"
                      label="Instruções do campo de resposta"
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Digite as instruções do campo de resposta"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmiting}
                >
                  Adicionar
                </Button>
              </Form>
            </>
          )
        }
      </div>
    </Modal>
  )
}
